import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Pencil, Trash2 } from 'lucide-react';
import { BlastComposer } from '../../components/blasts/BlastComposer';
import { BlastDetail } from '../../components/blasts/BlastDetail';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

type Tab = 'compose' | 'history' | 'drafts';

interface BlastSummary {
  _id: string;
  subject: string;
  audienceType: string;
  status: string;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  sentAt?: string;
  createdAt: string;
}

const audienceLabels: Record<string, string> = {
  all_ams: 'All AMs',
  all_physicians: 'All Physicians',
  all: 'All Users',
  pods: 'Pods'
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'sent':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Sent</span>;
    case 'failed':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Failed</span>;
    case 'sending':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Sending</span>;
    default:
      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{status}</span>;
  }
};

const BLAST_PAGE_SIZE = 100;
const BLAST_MAX_PAGES = 50;

export default function AdminBlasts() {
  const [activeTab, setActiveTab] = useState<Tab>('compose');
  const [blasts, setBlasts] = useState<BlastSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBlastId, setSelectedBlastId] = useState<string | null>(null);
  const [editingBlastId, setEditingBlastId] = useState<string | undefined>(undefined);

  // Every blast, walking /blasts page by page (it serves 50 by default). The
  // History and Drafts tabs split this list by status, so it must be complete:
  // one page silently hid older blasts and any draft past the newest 50.
  const fetchBlasts = useCallback(async () => {
    setLoading(true);
    try {
      const all: BlastSummary[] = [];
      for (let page = 0; page < BLAST_MAX_PAGES; page++) {
        const response = await apiClient.get<{ success: boolean; data: BlastSummary[]; pagination?: { hasMore?: boolean } }>(
          `/blasts?limit=${BLAST_PAGE_SIZE}&skip=${page * BLAST_PAGE_SIZE}`
        );
        if (!response.success) break;
        all.push(...response.data);
        if (!response.pagination?.hasMore || response.data.length === 0) break;
      }
      setBlasts(all);
    } catch (error) {
      console.error('Failed to fetch blasts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history' || activeTab === 'drafts') {
      fetchBlasts();
    }
  }, [activeTab, fetchBlasts]);

  const handleDeleteDraft = async (id: string) => {
    if (!confirm('Delete this draft?')) return;
    try {
      await apiClient.delete(`/blasts/${id}`);
      toast.success('Draft deleted');
      fetchBlasts();
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const handleEditDraft = (id: string) => {
    setEditingBlastId(id);
    setActiveTab('compose');
  };

  const handleBlastSaved = () => {
    // Refresh lists when navigating to them
  };

  const handleBlastSent = () => {
    setEditingBlastId(undefined);
    setActiveTab('history');
    fetchBlasts();
  };

  // Show detail view
  if (selectedBlastId) {
    return (
      <div className="rounded-xl shadow-sm border p-6" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <BlastDetail
          blastId={selectedBlastId}
          onBack={() => setSelectedBlastId(null)}
        />
      </div>
    );
  }

  const sentBlasts = blasts.filter(b => b.status !== 'draft');
  const draftBlasts = blasts.filter(b => b.status === 'draft');

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'compose', label: 'Compose' },
    { key: 'history', label: 'History', count: sentBlasts.length },
    { key: 'drafts', label: 'Drafts', count: draftBlasts.length }
  ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b" style={{ borderColor: 'var(--border-primary)' }}>
        <div className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key === 'compose') {
                  setEditingBlastId(undefined);
                }
              }}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent hover:border-gray-300'
              }`}
              style={activeTab !== tab.key ? { color: 'var(--text-tertiary)' } : undefined}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-100" style={{ color: 'var(--text-tertiary)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="rounded-xl shadow-sm border p-6" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        {activeTab === 'compose' && (
          <BlastComposer
            blastId={editingBlastId}
            onSaved={handleBlastSaved}
            onSent={handleBlastSent}
          />
        )}

        {activeTab === 'history' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : sentBlasts.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
                No blasts sent yet
              </div>
            ) : (
              <div className="space-y-3">
                {sentBlasts.map((blast) => (
                  <div
                    key={blast._id}
                    onClick={() => setSelectedBlastId(blast._id)}
                    className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {blast.subject}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {statusBadge(blast.status)}
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {audienceLabels[blast.audienceType] || blast.audienceType}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {blast.sentAt ? new Date(blast.sentAt).toLocaleDateString() : new Date(blast.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      <div>{blast.successCount}/{blast.totalRecipients} delivered</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'drafts' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : draftBlasts.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
                No drafts saved
              </div>
            ) : (
              <div className="space-y-3">
                {draftBlasts.map((blast) => (
                  <div
                    key={blast._id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    style={{ borderColor: 'var(--border-primary)' }}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {blast.subject}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {audienceLabels[blast.audienceType] || blast.audienceType}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          Created {new Date(blast.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditDraft(blast._id)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        title="Edit draft"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDraft(blast._id)}
                        className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                        style={{ color: 'var(--text-tertiary)' }}
                        title="Delete draft"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
